const inAppBrowserPattern =
  /\b(FBAN|FBAV|FB_IAB|FBIOS|Instagram|Zalo|ZaloTheme|musical_ly|Bytedance|BytedanceWebview|trill|TikTok)(?:\b|_)/i;

/**
 * @typedef {HTMLElement & {
 *   isPlaying?: boolean,
 *   pauseMedia?: () => void,
 *   playMedia?: () => void
 * }} ProductModelElement
 */

/**
 * @param {string} userAgent
 * @returns {boolean}
 */
export function isIOSBrowser(userAgent) {
  return /\b(iPhone|iPad|iPod)\b/i.test(userAgent);
}

/**
 * Detects embedded browsers that commonly cannot launch Shopify XR.
 * @param {string} userAgent
 * @returns {boolean}
 */
export function isInAppBrowser(userAgent) {
  const isAndroidWebView = /\bAndroid\b/i.test(userAgent) && /;\s?wv\)/i.test(userAgent);
  const isIOSWebView =
    isIOSBrowser(userAgent) && /AppleWebKit/i.test(userAgent) && !/Safari/i.test(userAgent);

  return isAndroidWebView || isIOSWebView || inAppBrowserPattern.test(userAgent);
}

/**
 * Shows guidance before supported Shopify XR launches and keeps AR controls
 * useful when Shopify XR leaves them unavailable. Known in-app browsers use
 * the fallback immediately. Other iOS browsers are only switched after
 * Shopify XR has had time to activate supported controls.
 * @param {{ forceFallback: boolean, detectFallback: boolean }} options
 */
function setupArBrowserFallback({ forceFallback, detectFallback }) {
  const arButtonSelector = '[data-shopify-xr][data-shopify-model3d-id]';
  let fallbackDetectionReady = forceFallback;
  let arPageWasHidden = false;
  /** @type {HTMLButtonElement | null} */
  let pendingArButton = null;
  /** @type {HTMLButtonElement | null} */
  let bypassArButton = null;
  /** @type {ProductModelElement[]} */
  let pausedModels = [];

  /** @param {ParentNode} root */
  const syncArButtons = (root) => {
    const buttons = [
      ...(root instanceof HTMLButtonElement && root.matches(arButtonSelector) ? [root] : []),
      ...root.querySelectorAll(arButtonSelector),
    ];

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;

      const shopifyArIsUnavailable = button.hasAttribute('data-shopify-xr-hidden');
      const shouldUseFallback = forceFallback || (fallbackDetectionReady && shopifyArIsUnavailable);

      if (shouldUseFallback) {
        button.hidden = false;
        button.setAttribute('data-ar-browser-fallback', '');
      } else if (!shopifyArIsUnavailable) {
        button.removeAttribute('data-ar-browser-fallback');
      }
    }
  };

  const finishFallbackDetection = () => {
    fallbackDetectionReady = true;
    syncArButtons(document);
  };

  const pauseProductModels = () => {
    pausedModels = [];
    arPageWasHidden = false;

    for (const model of document.querySelectorAll('product-model[data-media-loaded]')) {
      if (!(model instanceof HTMLElement)) continue;

      const productModel = /** @type {ProductModelElement} */ (model);
      if (productModel.isPlaying !== true) continue;

      const pauseMedia = productModel.pauseMedia;
      if (typeof pauseMedia !== 'function') continue;

      pauseMedia.call(productModel);
      pausedModels.push(productModel);
    }
  };

  const resumeProductModels = () => {
    if (!arPageWasHidden || document.visibilityState !== 'visible') return;

    for (const model of pausedModels) {
      if (!model.isConnected) continue;

      const playMedia = model.playMedia;
      if (typeof playMedia === 'function') playMedia.call(model);
    }

    pausedModels = [];
    arPageWasHidden = false;
  };

  /** @returns {HTMLDialogElement | null} */
  const getDialog = () => {
    const dialog = document.querySelector('[data-ar-browser-fallback-dialog]');
    return dialog instanceof HTMLElement && dialog.tagName === 'DIALOG'
      ? /** @type {HTMLDialogElement} */ (dialog)
      : null;
  };

  /** @param {HTMLDialogElement} dialog */
  const playDialogVideo = (dialog) => {
    const video = dialog.querySelector('[data-ar-dialog-media]:not([hidden]) [data-ar-dialog-video]');
    if (!(video instanceof HTMLVideoElement)) return;

    const media = video.closest('[data-ar-dialog-media]');
    const isGuideVideo = media instanceof HTMLElement && media.dataset.arDialogMedia === 'guide';

    const playMuted = () => {
      if (!dialog.open || !(media instanceof HTMLElement) || media.hidden) return;

      video.defaultMuted = true;
      video.muted = true;
      const mutedPlayPromise = video.play();
      if (mutedPlayPromise) void mutedPlayPromise.catch(() => {});
    };

    video.autoplay = true;
    video.playsInline = true;
    video.setAttribute('webkit-playsinline', '');
    video.defaultMuted = false;
    video.muted = false;
    video.volume = 0.8;
    video.loop = !isGuideVideo;
    video.onended = isGuideVideo
      ? () => {
          if (dialog.open && media instanceof HTMLElement && !media.hidden) launchPendingAr();
        }
      : null;
    if (video.currentTime > 0) video.currentTime = 0;
    const playPromise = video.play();
    if (playPromise) void playPromise.catch(playMuted);

    window.setTimeout(() => {
      if (video.paused) playMuted();
    }, 300);
  };

  /** @param {HTMLDialogElement} dialog */
  const pauseDialogVideos = (dialog) => {
    for (const video of dialog.querySelectorAll('[data-ar-dialog-video]')) {
      if (!(video instanceof HTMLVideoElement)) continue;

      video.onended = null;
      video.pause();
      if (video.currentTime > 0) video.currentTime = 0;
    }
  };

  /**
   * @param {HTMLDialogElement} dialog
   * @param {'fallback' | 'guide'} mode
   */
  const setDialogMode = (dialog, mode) => {
    const isFallback = mode === 'fallback';

    for (const title of dialog.querySelectorAll('[data-ar-dialog-title]')) {
      if (!(title instanceof HTMLElement)) continue;
      title.hidden = title.dataset.arDialogTitle !== mode;
    }

    for (const media of dialog.querySelectorAll('[data-ar-dialog-media]')) {
      if (!(media instanceof HTMLElement)) continue;
      media.hidden = media.dataset.arDialogMedia !== mode;
    }

    const message = dialog.querySelector('[data-ar-fallback-message]');
    if (message instanceof HTMLElement) message.hidden = true;

    const copyButton = dialog.querySelector('[data-ar-copy-link]');
    if (copyButton instanceof HTMLButtonElement) copyButton.hidden = true;

    const useCameraButton = dialog.querySelector('[data-ar-use-camera]');
    if (useCameraButton instanceof HTMLButtonElement) useCameraButton.hidden = isFallback;

    const copyStatus = dialog.querySelector('[data-ar-copy-status]');
    if (copyStatus instanceof HTMLElement) {
      copyStatus.hidden = true;
      copyStatus.textContent = '';
    }
  };

  /**
   * @param {'fallback' | 'guide'} mode
   * @param {HTMLButtonElement | null} [arButton]
   */
  const openDialog = (mode, arButton = null) => {
    const dialog = getDialog();
    if (!dialog) return;

    pendingArButton = mode === 'guide' ? arButton : null;
    pauseDialogVideos(dialog);
    setDialogMode(dialog, mode);

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    playDialogVideo(dialog);

    const actionButton = mode === 'guide' ? dialog.querySelector('[data-ar-use-camera]') : null;
    if (actionButton instanceof HTMLButtonElement) {
      actionButton.setAttribute('data-ar-initial-focus', '');
      actionButton.addEventListener('blur', () => actionButton.removeAttribute('data-ar-initial-focus'), {
        once: true,
      });
      actionButton.focus({ preventScroll: true });
    } else {
      dialog.focus({ preventScroll: true });
    }
  };

  /** @param {HTMLDialogElement} dialog */
  const closeDialog = (dialog) => {
    pendingArButton = null;
    pauseDialogVideos(dialog);

    if (typeof dialog.close === 'function') {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
    }
  };

  const launchPendingAr = () => {
    const dialog = getDialog();
    if (!dialog) return;

    const arButton = pendingArButton;
    if (!(arButton instanceof HTMLButtonElement) || !arButton.isConnected) {
      closeDialog(dialog);
      return;
    }

    if (arButton.hasAttribute('data-ar-browser-fallback')) {
      openDialog('fallback');
      return;
    }

    closeDialog(dialog);
    bypassArButton = arButton;
    arButton.click();
    if (bypassArButton === arButton) bypassArButton = null;
  };

  const copyPageLink = async () => {
    const dialog = getDialog();
    if (!dialog) return;

    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch {
      const input = document.createElement('textarea');
      input.value = window.location.href;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.append(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }

    const status = dialog.querySelector('[data-ar-copy-status]');
    if (status instanceof HTMLElement) status.textContent = status.dataset.copiedText || '';
  };

  document.documentElement.setAttribute('data-ar-browser-fallback-enabled', '');
  syncArButtons(document);

  if (detectFallback && !forceFallback) {
    document.addEventListener('shopify_xr_initialized', () => setTimeout(finishFallbackDetection, 100), {
      once: true,
    });

    if (document.readyState === 'complete') {
      setTimeout(finishFallbackDetection, 2500);
    } else {
      window.addEventListener('load', () => setTimeout(finishFallbackDetection, 2500), { once: true });
    }
  }

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes' && record.target instanceof HTMLButtonElement) {
        const button = record.target;
        if (button.matches(arButtonSelector)) syncArButtons(button);
      }

      for (const node of record.addedNodes) {
        if (node instanceof Element) syncArButtons(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['hidden', 'data-shopify-xr-hidden', 'data-shopify-model3d-id'],
  });

  document.addEventListener('visibilitychange', () => {
    if (pausedModels.length === 0) return;

    if (document.visibilityState === 'hidden') {
      arPageWasHidden = true;
    } else {
      resumeProductModels();
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (
        !(dialog instanceof HTMLElement) ||
        dialog.tagName !== 'DIALOG' ||
        !dialog.matches('[data-ar-browser-fallback-dialog]')
      ) {
        return;
      }

      pendingArButton = null;
      pauseDialogVideos(/** @type {HTMLDialogElement} */ (dialog));
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      if (event.target.closest('[data-ar-use-camera]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
        launchPendingAr();
        return;
      }

      const closeButton = event.target.closest('[data-ar-fallback-close]');
      const dialog = event.target.closest('[data-ar-browser-fallback-dialog]');
      if (closeButton && dialog instanceof HTMLElement && dialog.tagName === 'DIALOG') {
        if (pendingArButton instanceof HTMLButtonElement) {
          launchPendingAr();
        } else {
          closeDialog(/** @type {HTMLDialogElement} */ (dialog));
        }
        return;
      }

      if (event.target === dialog && dialog instanceof HTMLElement && dialog.tagName === 'DIALOG') {
        closeDialog(/** @type {HTMLDialogElement} */ (dialog));
        return;
      }

      if (event.target.closest('[data-ar-copy-link]')) void copyPageLink();

      const arButton = event.target.closest(arButtonSelector);
      if (!(arButton instanceof HTMLButtonElement)) return;

      if (arButton === bypassArButton) {
        bypassArButton = null;
        pauseProductModels();
        return;
      }

      if (!getDialog()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (arButton.hasAttribute('data-ar-browser-fallback')) {
        openDialog('fallback');
      } else {
        openDialog('guide', arButton);
      }
    },
    true
  );
}

if (typeof navigator !== 'undefined') {
  const isInApp = isInAppBrowser(navigator.userAgent);
  const isIOS = isIOSBrowser(navigator.userAgent);
  setupArBrowserFallback({ forceFallback: isInApp, detectFallback: isInApp || isIOS });
}
